import React, { createContext, useContext, useState, useEffect } from 'react';
import { getTenantFromSubdomain } from '../services/tenant';
import { isBulkMode, BULK_WEIGHT_MIN } from '../utils/weightEstimate';

const CartContext = createContext();

// Per-tenant cart key so two tenants' storefronts in the same browser do not
// share or clobber one cart. (Was a hardcoded 'hff_cart' — see C-1 audit F-C1-1.)
const cartStorageKey = () => `cart_${getTenantFromSubdomain()}`;
const LEGACY_CART_KEY = 'hff_cart';

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const key = cartStorageKey();

    // One-time migration: if a legacy single-tenant cart exists and the
    // tenant-scoped key is empty, adopt it then drop the legacy key.
    let raw = localStorage.getItem(key);
    if (!raw) {
      const legacy = localStorage.getItem(LEGACY_CART_KEY);
      if (legacy) {
        raw = legacy;
        try {
          localStorage.setItem(key, legacy);
        } catch (e) {
          // ignore quota/write errors — legacy value still parsed below
        }
        localStorage.removeItem(LEGACY_CART_KEY);
      }
    }

    if (raw) {
      try {
        setCartItems(JSON.parse(raw));
      } catch (e) {
        console.error('Error loading cart:', e);
        localStorage.removeItem(key);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(cartStorageKey(), JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, quantity = 1, selectedOption = null) => {
    setCartItems(prevItems => {
      // Create a unique identifier for this product + option combo
      const itemId = selectedOption 
        ? `${product.id}-${selectedOption.name}` 
        : `${product.id}`;
      
      const existingItemIndex = prevItems.findIndex(item => item.itemId === itemId);

      if (existingItemIndex > -1) {
        // Update quantity of existing item
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + quantity
        };
        return updatedItems;
      } else {
        // Add new item. Carries the Feature C C-4.1 / F-C1-10 enrichment fields
        // (sold_by, unit_of_measure, is_taxable, tax_rate, shipping_zone) set by
        // ProductDetail at add-to-cart time. These make the cart self-describing
        // for split-cart checkout (C-4.2), per-item tax, and shipping-by-zone
        // (C-4.3). Defaults below are defensive — they keep a cart item valid
        // even if an older/partial product object is passed (e.g. a future
        // quick-add that doesn't hydrate every field).
        return [...prevItems, {
          itemId,
          id: product.id,
          name: product.name,
          price: Number(selectedOption ? selectedOption.price : product.price),
          image: product.images ? product.images[0] : product.image,
          variant: selectedOption ? selectedOption.name : null,
          quantity,
          sold_by: product.sold_by || 'each',
          unit_of_measure: product.unit_of_measure || null,
          // avg_weight (F-WEIGHT-ESTIMATE Phase 2): typical package weight in the
          // item's UOM, carried so Cart/Checkout can render the catch-weight
          // estimate without re-fetching the item. Only 'package' items have a
          // value; null for bulk/each. Display-only — never charged.
          avg_weight: product.avg_weight !== undefined && product.avg_weight !== null
            ? Number(product.avg_weight)
            : null,
          is_taxable: product.is_taxable !== undefined ? Boolean(product.is_taxable) : true,
          tax_rate: product.tax_rate !== undefined && product.tax_rate !== null
            ? Number(product.tax_rate)
            : null,
          shipping_zone: product.shipping_zone || 'no-restrictions',
        }];
      }
    });

    // Briefly show cart indicator
    setIsCartOpen(true);
    setTimeout(() => setIsCartOpen(false), 2000);
  };

  const removeFromCart = (itemId) => {
    setCartItems(prevItems => prevItems.filter(item => item.itemId !== itemId));
  };

  const updateQuantity = (itemId, newQuantity) => {
    setCartItems(prevItems => {
      const item = prevItems.find(i => i.itemId === itemId);
      const minQty = item && isBulkMode(item) ? BULK_WEIGHT_MIN : 1;
      const qty = Number(newQuantity);

      if (!Number.isFinite(qty) || qty < minQty) {
        return prevItems.filter(i => i.itemId !== itemId);
      }

      const normalizedQty = isBulkMode(item)
        ? Math.round(qty * 100) / 100
        : Math.max(1, Math.floor(qty));

      return prevItems.map(i =>
        i.itemId === itemId ? { ...i, quantity: normalizedQty } : i
      );
    });
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (Number(item.price) * item.quantity), 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const value = {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export default CartContext;
